import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { devLog, devError } from '../utils/devLog';
import memoryApi from '../api/memoryApi';
import { closeAllPanels } from './panelCoordinator';

export default function useChatSessions({ user, messages, setMessages, isLoading, setIsLoading, setSidebarOpen, scrollToBottom, memoryManagerRef }) {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(urlSessionId || null);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(!!urlSessionId);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  // messageCacheRef removed — App.js's messagesBySession map is now the
  // per-session source of truth. Entries persist across session-switches
  // for the lifetime of the page; reload clears them.
  const prevUrlSessionIdRef = useRef(urlSessionId);

  // Sync currentSessionId with URL when navigating between sessions
  useEffect(() => {
    if (prevUrlSessionIdRef.current !== urlSessionId) {
      prevUrlSessionIdRef.current = urlSessionId;
    }
    if (urlSessionId !== currentSessionId) {
      setCurrentSessionId(urlSessionId || null);
    }
  }, [urlSessionId, currentSessionId]);

  // Load sessions on mount
  useEffect(() => {
    const loadSessions = async () => {
      if (!user) {
        // Either pre-login OR post-logout — clear any cached session list
        // so the sidebar/breadcrumb don't leak the previous user's chats.
        setSessions([]);
        setSessionsLoaded(false);
        setIsLoadingMessages(false);
        return;
      }

      devLog("🔧 SESSION DEBUG: Loading sessions for user:", user.name);

      try {
        const sessionList = await memoryApi.listSessions();
        devLog("🔧 SESSION DEBUG: API returned sessions:", sessionList?.length || 0);

        if (sessionList.length > 0) {
          setSessions(sessionList);
          if (urlSessionId && sessionList.some(s => s.session_id === urlSessionId)) {
            setCurrentSessionId(urlSessionId);
          } else if (urlSessionId) {
            setCurrentSessionId(urlSessionId);
          }
        } else {
          setSessions([]);
        }
        setSessionsLoaded(true);
      } catch (error) {
        devError("Error loading sessions:", error);
        setSessions([]);
        setSessionsLoaded(true);
      }
    };

    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load messages when session changes.
  // With per-session state, the bucket IS the source of truth — if the
  // bucket already has content (from a prior load OR an in-flight stream
  // writing to this session), skip the network call. Backend's
  // get_session_messages bumps `last_accessed` on every call, polluting
  // sidebar sort order; skipping on populated-bucket avoids that AND
  // avoids racing with concurrent stream writes.
  useEffect(() => {
    const loadSessionMessages = async () => {
      if (!currentSessionId) {
        closeAllPanels();
        return;
      }
      if (!sessionsLoaded) return;
      if (sessionStorage.getItem('pendingFirstMessage')) return;

      closeAllPanels();

      // Bucket already populated — either from a prior session-load this
      // tab or from a stream that's been writing to this session in the
      // background. Trust it; don't refetch. (Refetching here would race
      // with the stream and could overwrite optimistic user-message
      // appends with stale backend snapshots.)
      if (messages.length > 0) {
        setIsLoadingMessages(false);
        return;
      }

      const sanitizeMessages = (msgs) => msgs.map((msg) => {
        if (msg.text && msg.text.startsWith('{"') && msg.text.includes('"image":')) {
          try {
            const parsedContent = JSON.parse(msg.text);
            return { role: msg.role, text: parsedContent.text || "Look at this image and elaborate", image: parsedContent.image, timestamp: msg.timestamp };
          } catch (e) { return msg; }
        }
        if (msg.image && typeof msg.image === 'string' && msg.image.startsWith('blob:')) {
          const { image, ...rest } = msg;
          return rest;
        }
        return msg;
      });

      setIsLoadingMessages(true);
      devLog("🔧 SESSION DEBUG: Loading messages for session:", currentSessionId);

      try {
        const msgs = await memoryApi.getMessages(currentSessionId);
        devLog("🔧 SESSION DEBUG: API returned messages:", msgs?.length || 0);

        if (Array.isArray(msgs) && msgs.length > 0 && msgs.every(msg => msg.role && typeof msg.text === 'string')) {
          const sanitized = sanitizeMessages(msgs);
          setMessages(sanitized);
          if (memoryManagerRef.current && !memoryManagerRef.current.isInitialized) {
            memoryManagerRef.current.initFromMessages(sanitized);
            memoryManagerRef.current.isInitialized = true;
          }
          if (window.innerWidth > 1200) setTimeout(() => scrollToBottom(), 100);
        }
      } catch (error) {
        devError("Error loading session messages:", error);
      } finally {
        setIsLoadingMessages(false);
      }
    };

    loadSessionMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId, sessionsLoaded, setMessages]);

  // Session CRUD handlers
  const handleCreateSession = useCallback(() => {
    setCurrentSessionId(null);
    setMessages([]);
    setSidebarOpen(false);
    navigate('/chatty');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMessages, navigate]);

  const handleSelectSession = useCallback((sessionId) => {
    setCurrentSessionId(sessionId);
    setIsLoading(false);
    setSidebarOpen(false);
    navigate(`/chatty/${sessionId}`);
  }, [setIsLoading, navigate, setSidebarOpen]);

  const handleDeleteSession = useCallback(async (sessionId) => {
    try {
      await memoryApi.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));

      if (sessionId === currentSessionId) {
        setCurrentSessionId(null);
        setMessages([]);
        navigate('/chatty');
      }
    } catch (error) {
      devError("Failed to delete session:", error);
    }
  }, [currentSessionId, navigate, setMessages]);

  const handleRenameSession = useCallback(async (sessionId, title) => {
    try {
      await memoryApi.renameSession(sessionId, title);
      setSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, title } : s
      ));
    } catch (error) {
      devError("Failed to rename session:", error);
    }
  }, []);

  return {
    sessions, setSessions,
    currentSessionId, setCurrentSessionId,
    sessionsLoaded,
    isLoadingMessages,
    isCreatingSession, setIsCreatingSession,
    urlSessionId,
    navigate,
    handleCreateSession,
    handleSelectSession,
    handleDeleteSession,
    handleRenameSession,
  };
}
